import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col w-full"
}
const _hoisted_2 = { class: "text-xl font-bold text-gray-900 px-1" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col py-6 w-full"
}
const _hoisted_4 = { class: "-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" }
const _hoisted_5 = { class: "inline-block w-full py-2 align-middle sm:px-6 lg:px-8" }
const _hoisted_6 = { class: "overflow-hidden settingsCard p-0" }
const _hoisted_7 = { class: "divide-y divide-gray-200 sm:block" }
const _hoisted_8 = { class: "bg-gray-50 sm:grid-cols-12 sm:gap-2 items-center hidden sm:grid" }
const _hoisted_9 = { class: "px-3 py-3 col-span-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_10 = { class: "px-3 py-3 col-span-8 text-left text-xs font-medium uppercase tracking-wider text-gray-500" }
const _hoisted_11 = { class: "divide-y divide-gray-200 bg-white" }
const _hoisted_12 = ["onDblclick"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = {
  key: 1,
  class: "inline-flex items-center gap-x-1.5 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-600 max-w-max"
}
const _hoisted_15 = /*#__PURE__*/_createElementVNode("svg", {
  class: "h-1.5 w-1.5 fill-orange-400",
  viewBox: "0 0 6 6",
  "aria-hidden": "true"
}, [
  /*#__PURE__*/_createElementVNode("circle", {
    cx: "3",
    cy: "3",
    r: "3"
  })
], -1)
const _hoisted_16 = { class: "truncate text-gray-500" }
const _hoisted_17 = { class: "truncate text-gray-400" }
const _hoisted_18 = { class: "flex flex-wrap col-span-8 items-center whitespace-nowrap gap-1 px-3 text-sm text-gray-500" }
const _hoisted_19 = { class: "px-2 text-xs flex justify-self-end text-gray-500" }
const _hoisted_20 = { class: "mr-3 text-gray-400 cursor-pointer hover:text-gray-500" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", { class: "text-red-500 text-xs font-normal" }, "(Admin only)", -1)
const _hoisted_23 = { class: "pt-2 text-lg font-medium leading-6 text-gray-900" }
const _hoisted_24 = { class: "truncate text-gray-400" }
const _hoisted_25 = { class: "pt-4 flex items-center justify-center" }
const _hoisted_26 = {
  type: "submit",
  class: "focus:outline-none inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-8 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
}

import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon } from "@heroicons/vue/24/solid";
import { Form, SubmissionHandler } from "vee-validate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import LoadingSection from "shared/components/loading_state/LoadingSection.vue";
import Modal from "shared/components/modals/Modal.vue";
import { useCurrentCustomerName, useHasPermission } from "shared/composables/project";
import { useCurrentSiteId } from "shared/composables/project";
import { userRoles } from "shared/constants/user";
import { User, UserProject } from "shared/types/User";
import InputField from "@/components/forms/InputField.vue";
import SelectListField from "@/components/forms/SelectListField.vue";
import MainButton from "@/components/other/MainButton.vue";
import EditProjectPermissionModal from "@/components/users/EditProjectPermissionModal.vue";
import InviteUsersModal from "@/components/users/InviteUsersModal.vue";
import RemoveUserModal from "@/components/users/RemoveUserModal.vue";
import {
  useUsersForProject,
  useAdminUpdateUserProperties,
  useGetSortedFeaturePermissionsForUser,
} from "@/composables/user";


export default /*#__PURE__*/_defineComponent({
  __name: 'TabParticipants',
  setup(__props) {

const { t } = useI18n();
const hasAdminPermission = useHasPermission("app_admin");
const getSortedFeaturePermissionsForUser = useGetSortedFeaturePermissionsForUser();

const roleTypesList = userRoles.map((role) => ({
  value: role,
  name: t(`admin.project_participants.user_roles.${role}`),
}));

const permissionLabelsColors = {
  project_admin: "bg-green-100 text-green-800",
  all_camera_features_user: "bg-gray-300 text-gray-800",
  live_camera_feature_user: "bg-orange-200 text-yellow-800",
};

const customerName = useCurrentCustomerName();
const siteId = useCurrentSiteId();
const { users: allProjectUsers, isLoading } = useUsersForProject();

const { adminUpdateUserProperties } = useAdminUpdateUserProperties();

const selectedUser = ref<User | null>(null);

const isEditProjectPermissionModalOpen = ref(false);
const isDeleteUserModalOpen = ref(false);
const isInviteUsersModalOpen = ref(false);
const isAdminUpdateUserPropertiesModalOpen = ref(false);

const adminUpdatePropertiesSchema = yup.object().shape({
  name: yup.string().required(t("authentication.validation.validation_field_required")),
  company: yup.string().required(t("authentication.validation.validation_field_required")),
  role: yup.string().required(t("authentication.validation.validation_field_required")),
});

const projectUsers = computed(() =>
  allProjectUsers.value?.filter((user) => {
    const userProject = user.projects.find(
      (project) => project.customer_name === customerName && project.site_id === siteId,
    );
    return userProject && userProject.explicit_groups.length > 0;
  }),
);

const getProjectCompanyTypeForUser = (user: User) => {
  const project = user.projects.find(
    (project: UserProject) => project.customer_name === customerName && project.site_id === siteId,
  );
  if (!project) {
    return null;
  }
  return project.company_type;
};

const initializeUserProperties = (user: User) => {
  return {
    name: user.name,
    company: user.company,
    role: user.role || "",
  };
};

const openAdminUpdateUserPropertiesModal = (user: User) => {
  if (hasAdminPermission) {
    isAdminUpdateUserPropertiesModalOpen.value = true;
    selectedUser.value = user;
  }
};

const handleAdminUpdateUserProperties: SubmissionHandler = async (GenericObject) => {
  selectedUser.value &&
    adminUpdateUserProperties({
      username: selectedUser.value.username,
      name: GenericObject.name,
      company: GenericObject.company,
      role: GenericObject.role,
    });
  isAdminUpdateUserPropertiesModalOpen.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (!_unref(isLoading))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t("admin.project_settings.tab_project_participants")), 1),
          (projectUsers.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", _hoisted_8, [
                          _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t("admin.project_participants.table_user")), 1),
                          _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t("admin.project_participants.table_permissions")), 1)
                        ]),
                        _createElementVNode("div", _hoisted_11, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(projectUsers.value, (projectUser) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: projectUser._id,
                              class: "grid sm:grid-cols-12 items-center xl:gap-4 gap-2 py-4"
                            }, [
                              _createElementVNode("div", {
                                onDblclick: ($event: any) => (openAdminUpdateUserPropertiesModal(projectUser)),
                                class: "px-3 col-span-3 flex flex-col 2xl:text-sm text-xs break-all font-medium text-gray-900"
                              }, [
                                (projectUser.user_status === 'confirmed')
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(projectUser.name), 1))
                                  : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                      _hoisted_15,
                                      _createTextVNode(" " + _toDisplayString(_ctx.$t("admin.project_participants.pending_invite")), 1)
                                    ])),
                                _createElementVNode("small", _hoisted_16, _toDisplayString(projectUser.email), 1),
                                _createElementVNode("small", _hoisted_17, _toDisplayString(projectUser.role
                          ? _ctx.$t(`admin.project_participants.user_roles.${projectUser.role}`) +
                            (getProjectCompanyTypeForUser(projectUser)
                              ? ` (${_ctx.$t(
                                  `admin.project_participants.company_types.${getProjectCompanyTypeForUser(
                                    projectUser,
                                  )}`,
                                )})`
                              : "")
                          : getProjectCompanyTypeForUser(projectUser)
                          ? `(${_ctx.$t(
                              `admin.project_participants.company_types.${getProjectCompanyTypeForUser(
                                projectUser,
                              )}`,
                            )})`
                          : ""), 1)
                              ], 40, _hoisted_12),
                              _createElementVNode("div", _hoisted_18, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getSortedFeaturePermissionsForUser)(projectUser), (permission) => {
                                  return (_openBlock(), _createElementBlock("div", { key: permission }, [
                                    (permission !== 'project_base')
                                      ? (_openBlock(), _createElementBlock("div", {
                                          key: 0,
                                          class: _normalizeClass(["inline-flex rounded-full px-2 text-xs font-semibold leading-5", 
                          permission in permissionLabelsColors
                            ? permissionLabelsColors[permission as 'project_admin' | 'all_camera_features_user' | 'live_camera_feature_user']
                            : 'bg-yellow-300 text-yellow-800'
                        ])
                                        }, _toDisplayString(_ctx.$t(`permissions.${permission}`)), 3))
                                      : _createCommentVNode("", true)
                                  ]))
                                }), 128))
                              ]),
                              _createElementVNode("div", _hoisted_19, [
                                _createElementVNode("div", _hoisted_20, [
                                  _createVNode(_unref(PencilIcon), {
                                    onClick: _withModifiers(($event: any) => {
                          isEditProjectPermissionModalOpen.value = true;
                          selectedUser.value = projectUser;
                        }, ["prevent"]),
                                    id: "feature-add-button",
                                    class: "h-4",
                                    "aria-hidden": "true"
                                  }, null, 8, ["onClick"])
                                ]),
                                _createElementVNode("div", {
                                  onClick: ($event: any) => {
                        isDeleteUserModalOpen.value = true;
                        selectedUser.value = projectUser;
                      },
                                  class: "block text-gray-400 cursor-pointer hover:text-gray-500"
                                }, [
                                  _createVNode(_unref(TrashIcon), { class: "h-4" })
                                ], 8, _hoisted_21)
                              ])
                            ]))
                          }), 128))
                        ])
                      ])
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: _normalizeClass({ 'mt-6': !projectUsers.value.length })
          }, [
            _createVNode(MainButton, {
              color: "yellow",
              label: _ctx.$t('admin.project_participants.add_participants'),
              onClicked: _cache[0] || (_cache[0] = ($event: any) => (isInviteUsersModalOpen.value = true))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(PlusIcon), {
                  class: "-ml-1 mr-3 h-5 w-5",
                  "aria-hidden": "true"
                })
              ]),
              _: 1
            }, 8, ["label"])
          ], 2)
        ]))
      : (_openBlock(), _createBlock(LoadingSection, {
          key: 1,
          loading: _unref(isLoading)
        }, null, 8, ["loading"])),
    (selectedUser.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 2,
          open: isAdminUpdateUserPropertiesModalOpen.value,
          onClose: _cache[1] || (_cache[1] = ($event: any) => (isAdminUpdateUserPropertiesModalOpen.value = false)),
          "custom-cls": "sm:w-full sm:max-w-lg"
        }, {
          title: _withCtx(() => [
            _hoisted_22,
            _createElementVNode("div", null, [
              _createElementVNode("h3", _hoisted_23, _toDisplayString(selectedUser.value.name), 1),
              _createElementVNode("small", _hoisted_24, _toDisplayString(selectedUser.value.email), 1)
            ])
          ]),
          content: _withCtx(() => [
            _createElementVNode("div", null, [
              _createVNode(_unref(Form), {
                onSubmit: handleAdminUpdateUserProperties,
                initialValues: initializeUserProperties(selectedUser.value),
                validationSchema: _unref(adminUpdatePropertiesSchema),
                class: "flex flex-col gap-2 w-full"
              }, {
                default: _withCtx(() => [
                  _createVNode(InputField, {
                    name: "name",
                    label: _ctx.$t('authentication.sign_up.name')
                  }, null, 8, ["label"]),
                  _createVNode(InputField, {
                    name: "company",
                    label: _ctx.$t('authentication.sign_up.company')
                  }, null, 8, ["label"]),
                  _createVNode(SelectListField, {
                    name: "role",
                    label: _ctx.$t('authentication.sign_up.role'),
                    options: _unref(roleTypesList),
                    minWidth: 250,
                    placeholder: `--
              ${_ctx.$t('form.select_option')} --`
                  }, null, 8, ["label", "options", "placeholder"]),
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("button", _hoisted_26, [
                      _createVNode(_unref(ArrowPathIcon), {
                        class: "-ml-1 mr-3 h-5 w-5",
                        "aria-hidden": "true"
                      }),
                      _createTextVNode(" " + _toDisplayString(_ctx.$t("admin.project_participants.modal_permission.update")), 1)
                    ])
                  ])
                ]),
                _: 1
              }, 8, ["initialValues", "validationSchema"])
            ])
          ]),
          _: 1
        }, 8, ["open"]))
      : _createCommentVNode("", true),
    (isInviteUsersModalOpen.value)
      ? (_openBlock(), _createBlock(InviteUsersModal, {
          key: 3,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (isInviteUsersModalOpen.value = false)),
          existingUsers: projectUsers.value
        }, null, 8, ["existingUsers"]))
      : _createCommentVNode("", true),
    (isDeleteUserModalOpen.value && selectedUser.value)
      ? (_openBlock(), _createBlock(RemoveUserModal, {
          key: 4,
          user: selectedUser.value,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (isDeleteUserModalOpen.value = false))
        }, null, 8, ["user"]))
      : _createCommentVNode("", true),
    (isEditProjectPermissionModalOpen.value && selectedUser.value)
      ? (_openBlock(), _createBlock(EditProjectPermissionModal, {
          key: 5,
          user: selectedUser.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (isEditProjectPermissionModalOpen.value = false))
        }, null, 8, ["user"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})